/**
 * Results of a judged test play (W2-02; DESIGN.md §4.9, ARCHITECTURE.md §7.5): accuracy, score, max combo,
 * Perfect / Good / Bad / Miss counts, early / late counts and the early/late histogram, whose bars jump to their notes
 * in the editor. When the hits are more than 20 ms early or late on average, a hint links to Song & Timing and to
 * latency calibration; the chart offset is never changed automatically.
 */

import { useT } from '../../i18n/useT';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import type { NoteId } from '../../core/model/types';
import { LIMIT_GOOD, LIMIT_PERFECT, needsTimingHint, type HistogramBin, type JudgeResults as Results } from './judge';
import { m } from './preview.messages';
import s from './Preview.module.css';

export interface JudgeResultsProps {
  readonly results: Results;
  readonly onRestart: () => void;
  readonly onBackHere: () => void;
  readonly onBackStart: () => void;
  /** Leaves test play and selects these notes (a histogram bar). */
  readonly onJump: (notes: readonly NoteId[]) => void;
  /** Song & Timing workspace (null = not available). */
  readonly onOpenTiming: (() => void) | null;
  /** Latency calibration (null = not available). */
  readonly onCalibrate: (() => void) | null;
}

function zoneOf(bin: HistogramBin): 'perfect' | 'good' | 'bad' {
  const mid = Math.abs((bin.from + bin.to) / 2);
  if (mid <= LIMIT_PERFECT) return 'perfect';
  if (mid <= LIMIT_GOOD) return 'good';
  return 'bad';
}

function ms(sec: number): number {
  return Math.round(sec * 1000);
}

export function JudgeResults(props: JudgeResultsProps) {
  const { results: r } = props;
  const t = useT();
  const peak = Math.max(1, ...r.histogram.map((b) => b.notes.length));
  const mean = r.meanOffset;
  const hint = needsTimingHint(r) && mean !== null;
  return (
    <section className={`${s.card ?? ''} ${s.cardWide ?? ''}`} aria-labelledby="preview-results-title" data-results="">
      <h2 id="preview-results-title" className={s.cardTitle}>
        {t(m.resultsTitle)}
      </h2>
      {r.total === 0 ? (
        <p>{t(m.resultsNoNotes)}</p>
      ) : (
        <dl className={s.stats}>
          <div className={s.stat}>
            <dt>{t(m.accuracy)}</dt>
            <dd data-stat="accuracy">{t(m.accuracyValue, { value: (r.accuracy * 100).toFixed(2) })}</dd>
          </div>
          <div className={s.stat}>
            <dt>{t(m.score)}</dt>
            <dd data-stat="score">{String(r.score).padStart(7, '0')}</dd>
          </div>
          <div className={s.stat}>
            <dt>{t(m.maxCombo)}</dt>
            <dd data-stat="maxCombo">{r.maxCombo}</dd>
          </div>
          <div className={`${s.stat ?? ''} ${s.statPerfect ?? ''}`}>
            <dt>{t(m.gradePerfect)}</dt>
            <dd data-stat="perfect">{r.perfect}</dd>
          </div>
          <div className={`${s.stat ?? ''} ${s.statGood ?? ''}`}>
            <dt>{t(m.gradeGood)}</dt>
            <dd data-stat="good">{r.good}</dd>
          </div>
          <div className={`${s.stat ?? ''} ${s.statBad ?? ''}`}>
            <dt>{t(m.gradeBad)}</dt>
            <dd data-stat="bad">{r.bad}</dd>
          </div>
          <div className={`${s.stat ?? ''} ${s.statMiss ?? ''}`}>
            <dt>{t(m.gradeMiss)}</dt>
            <dd data-stat="miss">{r.miss}</dd>
          </div>
          <div className={s.stat}>
            <dt>{t(m.earlyLate)}</dt>
            <dd data-stat="earlyLate">{t(m.earlyLateValue, { early: r.early, late: r.late })}</dd>
          </div>
          {mean !== null && (
            <div className={s.stat}>
              <dt>{t(m.meanOffset)}</dt>
              <dd data-stat="mean">
                {t(m.meanValue, { ms: Math.abs(ms(mean)), dir: mean < 0 ? 'early' : mean > 0 ? 'late' : 'none' })}
              </dd>
            </div>
          )}
          {r.pending > 0 && (
            <div className={s.stat}>
              <dt>{t(m.pending)}</dt>
              <dd data-stat="pending">{r.pending}</dd>
            </div>
          )}
        </dl>
      )}
      {r.offsets.length > 0 && (
        <div className={s.histogram} role="group" aria-label={t(m.histogramLabel)}>
          <div className={s.histogramBars}>
            {r.histogram.map((bin) => {
              const count = bin.notes.length;
              const label = t(m.histogramBar, { count, from: ms(bin.from), to: ms(bin.to) });
              return (
                <button
                  key={bin.from}
                  type="button"
                  className={s.bar}
                  data-zone={zoneOf(bin)}
                  aria-label={label}
                  title={label}
                  disabled={count === 0}
                  onClick={() => {
                    props.onJump(bin.notes);
                  }}
                >
                  <span className={s.barFill} style={{ height: `${String(Math.round((count / peak) * 100))}%` }} />
                </button>
              );
            })}
          </div>
          <div className={s.histogramAxis} aria-hidden="true">
            <span>{t(m.histogramEarly, { ms: ms(r.histogram[0]?.from ?? 0) })}</span>
            <span>0</span>
            <span>{t(m.histogramLate, { ms: ms(r.histogram[r.histogram.length - 1]?.to ?? 0) })}</span>
          </div>
        </div>
      )}
      {hint && (
        <div className={s.timingHint} role="note" data-timing-hint="">
          <Icon name="Info" size={16} />
          <div>
            <p>{t(m.timingHint, { ms: Math.abs(ms(mean)), dir: mean < 0 ? 'early' : 'late' })}</p>
            <div className={s.cardRow}>
              {props.onOpenTiming !== null && (
                <Button size="sm" icon="AudioWaveform" onClick={props.onOpenTiming}>
                  {t(m.openTiming)}
                </Button>
              )}
              {props.onCalibrate !== null && (
                <Button size="sm" icon="Gauge" onClick={props.onCalibrate}>
                  {t(m.calibrate)}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={s.cardRow}>
        <Button variant="primary" icon="RotateCcw" autoFocus onClick={props.onRestart}>
          {t(m.restart)}
        </Button>
        <Button icon="MapPin" onClick={props.onBackHere}>
          {t(m.backHere)}
        </Button>
        <Button icon="Undo2" onClick={props.onBackStart}>
          {t(m.backStart)}
        </Button>
      </div>
    </section>
  );
}
